import React, { useState, useRef, useEffect } from 'react';
import { FileSpreadsheet, ExternalLink } from "lucide-react";
import { Tooltip } from 'components/atoms/Base/Logo/Tooltip';
import { UserData } from 'common/models/navigation';

const profilePlaceholderSrc = '/logos/profilelogo.png';
import { SignOutButton } from 'components/atoms/Custom/SignOutButton/SignOutButton';
import { PrimaryRoutes } from 'common/enums/internal-route-enums';
import { useTrackingApiService } from 'api/providers/AppServiceProvider';
import { DeviceIdHelper } from 'common/helpers/device-id-helper';
import { useMsal } from '@azure/msal-react';

interface UserProfileProps {
  user: UserData;
  collapsed?: boolean;
  onSettings?: () => void;
  isLoading?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({ 
  user, 
  collapsed = false,
  isLoading = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const trackingService = useTrackingApiService();
  const { accounts } = useMsal();
  const deviceId = useRef<string>(DeviceIdHelper.getOrCreateDeviceId());

  const handleExportCleanerClick = () => {
    // Open the page first
    window.open(PrimaryRoutes.ExportCleaner, '_blank', 'noopener,noreferrer');
    setIsDropdownOpen(false);

    // Track the click if user is authenticated
    if (accounts.length === 0) {
      console.warn('User not authenticated, skipping tracking');
      return;
    }

    if (!trackingService) {
      console.error('Tracking service not available');
      return;
    }

    const pageUrl = window.location.href;
    
    trackingService.TrackClick(
      'Export Cleaner',
      'export-cleaner-button',
      pageUrl,
      deviceId.current
    ).catch((error) => {
      console.error('Failed to track Export Cleaner click:', error);
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (collapsed) {
    return (
      <div 
        className={`mt-auto ${collapsed ? 'p-3' : 'p-2'} relative`} 
        ref={dropdownRef}
      >
        <Tooltip text={user.name} position="right">
          <div 
            className="w-6 h-6 mx-auto rounded-full overflow-hidden flex items-center justify-center bg-white border border-tertiary-background cursor-pointer hover:opacity-90 transition-opacity"
            onClick={toggleDropdown}
          >
            <img
              src={profilePlaceholderSrc}
              alt=""
              className="size-full object-cover object-center"
              draggable={false}
            />
          </div>
        </Tooltip>

        {/* Dropdown menu for collapsed state */}
        {isDropdownOpen && (
          <div className="fixed bottom-16 left-4 w-[180px] rounded-[24px] shadow-elev-3-popover border border-tertiary-background bg-white p-2 z-[99999]">
            {/* <div className="px-3 py-2">
              <div className="text-ui text-light-grey">App version 2.13.19</div>
            </div> */}
            
            {/* <button
              onClick={() => {
                onSettings?.();
                setIsDropdownOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-ui text-primary-text hover:bg-tertiary-background rounded-[36px] transition-colors"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button> */}

            <button
              onClick={handleExportCleanerClick}
              className="w-full flex items-center gap-3 px-3 py-2 text-ui text-primary-text hover:bg-tertiary-background rounded-[36px] transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="flex items-center gap-1.5">
                Export Cleaner
                <ExternalLink className="w-3 h-3 opacity-60" />
              </span>
            </button>
            
            <SignOutButton isLoading={isLoading} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className="mt-auto relative" 
      ref={dropdownRef}
    >
      <div 
        className="flex items-center space-x-3 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer rounded-lg p-2 -m-2"
        onClick={toggleDropdown}
      >
        <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center bg-white border border-tertiary-background shrink-0">
          <img
            src={profilePlaceholderSrc}
            alt=""
            className="size-full object-cover object-center"
            draggable={false}
          />
        </div>
        <div className="flex-1 min-w-0 transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]">
          <p
            className="
              text-ui tracking-[-0.02em]
              text-primary-text truncate
            "
          >
            {user.name}
          </p>
        </div>
      </div>

      {/* Dropdown menu for expanded state */}
      {isDropdownOpen && (
        <div
          className="fixed bottom-16 left-6 w-[190px] bg-white border border-tertiary-background rounded-[24px] shadow-elev-3-popover p-2 z-[99999]"
        >
          {/* <div className="px-3 py-2">
            <div className="text-ui text-light-grey">App version 2.13.19</div>
          </div>
          
          <button
            onClick={() => {
              onSettings?.();
              setIsDropdownOpen(false);
            }}
            className="w-full flex items-center gap-3 px-3 py-2 text-ui text-primary-text hover:bg-tertiary-background rounded-[36px] transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button> */}

          <button
            onClick={handleExportCleanerClick}
            className="w-full flex items-center gap-3 px-3 py-2 text-ui text-primary-text hover:bg-tertiary-background rounded-[36px] transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="flex items-center gap-1.5">
              Export Cleaner
              <ExternalLink className="w-3 h-3 opacity-60" />
            </span>
          </button>
          
          <SignOutButton isLoading={isLoading} />
        </div>
      )}
    </div>
  );
};
